import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
} from '@mui/material';
import { trialOptionsDialog } from '../utils/subscriptionCopy';

/**
 * What happens after the trial, behind "Choose a Plan" on the Active Plan card.
 *
 * REQUESTED. The button navigated straight to the plan grid, which answers
 * "which subscription?" — a question nobody on a trial has necessarily reached
 * yet. The trial ends on the free Basic plan whatever they do, nothing is
 * charged and nothing is removed, and someone deciding under the impression
 * that their work is about to be taken away is deciding on a false premise.
 * So the dialog says that first, and offers the two routes onward second.
 *
 * ── IT IS A DECISION POINT, WHICH IS WHY IT HAS TWO BUTTONS ─────────────────
 * The other dialog on this card — TrialFeaturesDialog — deliberately carries
 * only Close, because "what am I getting?" is a reference question and an
 * upgrade button there would turn reading into a sales prompt. This one is the
 * opposite case: it is opened BY a call to action, so it has to end in the
 * actions, and both are offered with equal weight rather than one filled and
 * one text link. Which of the two is right depends on how often you publish,
 * not on which we would rather sell.
 *
 * ── WHY IT DOES NOT REUSE PlanOptionsCard ──────────────────────────────────
 * That card puts the same choice to someone who has already arrived on Manage
 * Plan to buy. This is read by someone whose trial is ending, so it opens with
 * what happens if they do nothing and carries the credit-value comparison the
 * card does not. The wording is separate on purpose — see trialOptionsDialog
 * in subscriptionCopy, where both live and are tested.
 */
const TEXT = '#1A1A1A';
const TEXT_MUTED = '#5A5F55';
const ORANGE = '#FF7A59';
const ORANGE_HOVER = '#FF5722';
const GREEN = '#006A63';
const GREEN_TINT = 'rgba(0,106,99,0.06)';

export default function TrialOptionsDialog({
  open,
  onClose,
  /** Formatted trial end date, or null — the copy drops the clause without it. */
  trialEndsOn = null,
  onGetSubscription,
  onBuyCreditPack,
}) {
  const copy = trialOptionsDialog({ trialEndsOn });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="trial-options-title"
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
    >
      <DialogTitle
        id="trial-options-title"
        sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 20, pb: 1 }}
      >
        {copy.title}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* The reassurance leads, before either option is named. */}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: 1.6, color: TEXT, mb: 2,
          }}
        >
          {copy.intro}
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 700, color: TEXT, mb: 1.5,
          }}
        >
          {copy.lead}
        </Typography>

        {/* Each option is one paragraph led by its name in bold — the same
            relationship the buttons below have to each other, so the reader can
            match a paragraph to a button without either being numbered. */}
        {copy.options.map((option) => (
          <Box key={option.key} sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: 1.6, color: TEXT,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>{option.label}</Box>
              {` ${option.text}`}
            </Typography>

            {/* Set apart, like the options card's footnote: it is the cost of
                the option rather than another of its reassurances. */}
            {option.note && (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: TEXT_MUTED,
                  fontStyle: 'italic',
                  mt: 0.75,
                }}
              >
                {option.note}
              </Typography>
            )}
          </Box>
        ))}
      </DialogContent>

      {/* Both actions, in the order the paragraphs introduce them. Filled
          orange for the subscription and outlined green for packs is the
          vocabulary the plan cards already use for these two things — not a
          ranking invented here. */}
      <DialogActions sx={{ p: 2, pt: 1, gap: 1.25, flexWrap: 'wrap' }}>
        <Button
          onClick={onGetSubscription}
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            py: 1,
            color: '#FFFFFF',
            background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_HOVER} 100%)`,
            boxShadow: '0 4px 14px rgba(255, 87, 34, 0.35)',
            '&:hover': {
              background: `linear-gradient(135deg, ${ORANGE_HOVER} 0%, #E64A19 100%)`,
              boxShadow: '0 6px 20px rgba(255, 87, 34, 0.45)',
            },
          }}
        >
          {copy.options[0].buttonLabel}
        </Button>

        <Button
          onClick={onBuyCreditPack}
          variant="outlined"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            py: 1,
            bgcolor: '#FFFFFF',
            borderColor: GREEN,
            color: GREEN,
            '&:hover': { borderColor: GREEN, bgcolor: GREEN_TINT },
          }}
        >
          {copy.options[1].buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
